import { t } from "@/i18n";
import {
  getPreference,
  setPreference,
} from "@/lib/capacitor/preferences";

export type NotificationPrefs = {
  enabled: boolean;
  /** Local hour 0-23 for soft daily reminder (web Notification / future native) */
  hour: number;
  /** Last time we showed a local soft reminder (ISO) */
  lastRemindedAt: string | null;
};

const KEY = "notification_prefs";

const DEFAULTS: NotificationPrefs = {
  enabled: false,
  hour: 9,
  lastRemindedAt: null,
};

export async function loadNotificationPrefs(): Promise<NotificationPrefs> {
  const raw = await getPreference(KEY);
  if (!raw) return { ...DEFAULTS };
  try {
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<NotificationPrefs>) };
  } catch {
    return { ...DEFAULTS };
  }
}

export async function saveNotificationPrefs(
  prefs: NotificationPrefs
): Promise<void> {
  await setPreference(KEY, JSON.stringify(prefs));
}

/**
 * Soft local reminder stub: browser Notification if permission granted.
 * No-ops when disabled or already reminded today.
 */
export async function maybeShowLocalReminder(): Promise<boolean> {
  const prefs = await loadNotificationPrefs();
  if (!prefs.enabled) return false;
  if (typeof window === "undefined") return false;
  if (typeof Notification === "undefined") return false;

  const now = new Date();
  if (now.getHours() < prefs.hour) return false;

  if (prefs.lastRemindedAt) {
    const last = new Date(prefs.lastRemindedAt);
    if (last.toDateString() === now.toDateString()) return false;
  }

  if (Notification.permission === "denied") return false;
  if (Notification.permission === "default") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return false;
  }

  try {
    new Notification(t("push.dailyInsightTitle"), {
      body: t("push.dailyInsightBody"),
      tag: "andrum-daily-insight",
    });
    await saveNotificationPrefs({
      ...prefs,
      lastRemindedAt: now.toISOString(),
    });
    return true;
  } catch {
    return false;
  }
}


/**
 * Local soft push for today's insight (web Notification).
 * Prefers insight title when fetch works; falls back to i18n.
 */
export async function maybeShowLocalInsightReminder(): Promise<boolean> {
  const prefs = await loadNotificationPrefs();
  if (!prefs.enabled) return false;
  if (typeof window === "undefined") return false;
  if (typeof Notification === "undefined") return false;

  const now = new Date();
  if (now.getHours() < prefs.hour) return false;

  if (prefs.lastRemindedAt) {
    const last = new Date(prefs.lastRemindedAt);
    if (last.toDateString() === now.toDateString()) return false;
  }

  if (Notification.permission === "denied") return false;
  if (Notification.permission === "default") {
    // Do not request here — only show if already granted
    return false;
  }
  if (Notification.permission !== "granted") return false;

  let title = t("push.dailyInsightTitle");
  let body = t("push.dailyInsightBody");
  try {
    const { fetchDailyInsight } = await import(
      "@/features/learn/api/insights-api"
    );
    const daily = await fetchDailyInsight();
    if (daily?.insight?.title) {
      title = t("push.dailyInsightTitle");
      body = daily.insight.title;
    }
  } catch {
    // keep fallback
  }

  try {
    new Notification(title, {
      body,
      tag: "andrum-daily-insight",
    });
    await saveNotificationPrefs({
      ...prefs,
      lastRemindedAt: now.toISOString(),
    });
    return true;
  } catch {
    return false;
  }
}
